import { flow, pipe } from 'fp-ts/function';
import { every, get, groupBy, keys, map, minBy, sortBy, values } from 'lodash/fp';
import { createSelector } from 'reselect';
import { array, option, record } from 'fp-ts';
import { OrderItemType, ServiceOrder } from '@mews-commander/data-access';
import { ApplicationState, isProductOrderItem, OrderItem } from '@mews-commander/types';
import { getCollectionIds, getProp, getStringArrayProp, getStringProp, ifElse } from '@mews/fp';
import { composeProps, createPropsExtender } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { getFeatureFlagService } from '@mews-commander/legacy-app-instance';
import {
    getServiceOrderIdFromProps,
    getServiceOrderServiceId,
} from '@mews-commander/legacy-entities/src/selectors/serviceOrders';
import { getService } from '@mews-commander/legacy-entities/src/ducks/services';
import { getServiceOrdersByIds } from '@mews-commander/legacy-entities/src/ducks/serviceOrders';
import { isProductServiceOrder } from '@mews-commander/legacy-entities/src/ducks/serviceOrderNew';
import { mapTypeToGroupingFunc } from '../utils';
import { getReservationIdsByBillIds } from '../ducks/reservations';
import { getOrderItemsGroupedByService } from './groupingHelpers';
import {
    getGroupableOrderItemsByBillId,
    getMonthOrdersServiceOrders,
    getOrderedMonthServiceOrders,
} from './orderItems';

const getTypeFromProps = getProp<OrderItemType>()('type');

// @ts-expect-error $TSFixMeError: Argument of type '(state: ApplicationState, props: { serviceOrderId: string; }) => { serviceOrderId: string; } & Extende...
export const getServiceByServiceOrderId = composeProps(getService, createPropsExtender('id', getServiceOrderServiceId));

const getOrderItemsGroupedByServiceId = createSelector<
    ApplicationState,
    { serviceId: string; billId: string; month: string },
    string,
    _.Dictionary<OrderItem[]>,
    OrderItem[]
>(getStringProp('serviceId'), getOrderItemsGroupedByService, get);

const getOrderItemsGroupedByServiceIds = createSelector(
    getStringArrayProp('serviceIds'),
    getOrderItemsGroupedByService,
    (serviceIds, orderItemsGroupedByService) =>
        pipe(
            orderItemsGroupedByService,
            record.toArray,
            array.filterMap(([serviceId, orderItems]) =>
                serviceIds.includes(serviceId) ? option.some(orderItems) : option.none
            ),
            array.flatten
        )
);

const getOrderItemsGroupedByServiceOrderId = createSelector(
    getOrderItemsGroupedByServiceId,
    groupBy<OrderItem>('serviceOrderId')
);

const getBillOrderItemsGroupedByServiceOrderIdByServiceIds = createSelector(
    getOrderItemsGroupedByServiceIds,
    groupBy<OrderItem>('serviceOrderId')
);

export const getBillOrderItemsByServiceOrderId = createSelector(
    getServiceOrderIdFromProps,
    getOrderItemsGroupedByServiceOrderId,
    (serviceOrderId, groupedByServiceOrderId) => get(serviceOrderId, groupedByServiceOrderId)
);

export const getBillOrderItemsByServiceOrderIds = createSelector(
    getStringArrayProp('serviceOrderIds'),
    getBillOrderItemsGroupedByServiceOrderIdByServiceIds,
    (serviceOrderIds, groupedByServiceOrderId) =>
        pipe(
            groupedByServiceOrderId,
            record.toArray,
            array.filterMap(([serviceOrderId, orderItems]) =>
                serviceOrderIds.includes(serviceOrderId) ? option.some(orderItems) : option.none
            ),
            array.flatten
        )
);

const orderServiceOrderItems = flow(
    groupBy<OrderItem>('serviceOrderId'),
    values,
    map(minBy('consumedUtc')),
    sortBy('consumedUtc'),
    map('serviceOrderId')
);

export const getOrderedServiceOrderIds = createSelector(getGroupableOrderItemsByBillId, orderServiceOrderItems);

export const getOrderedServiceOrders = composeProps(
    getServiceOrdersByIds,
    createPropsExtender('ids', getOrderedServiceOrderIds)
);

const isMonthSet = createSelector(getProp<string | undefined>()('month'), (month) => !!month);
const getBillOrMonthOrderItems = ifElse(isMonthSet, getOrderedMonthServiceOrders, getOrderedServiceOrders);

export const getProductServiceOrdersByReservationId = createSelector(
    getStringProp('reservationId'),
    getBillOrMonthOrderItems,
    (reservationId, serviceOrders) =>
        pipe(
            serviceOrders,
            array.filter(
                (serviceOrder) =>
                    isProductServiceOrder(serviceOrder) && serviceOrder.data.value.linkedReservationId === reservationId
            )
        )
);

export const getNestedServiceIds = createSelector(
    getFeatureFlagService,
    getProductServiceOrdersByReservationId,
    getStringProp('serviceId'),
    (featureFlagService, nestedProductServiceOrders, serviceId) =>
        featureFlagService.hasFlag(FeatureFlag.linkProductsToReservationsEnabled)
            ? [...nestedProductServiceOrders.map((ps) => ps.serviceId), serviceId]
            : [serviceId]
);

export const getNestedProductServiceIds = createSelector(
    getFeatureFlagService,
    getProductServiceOrdersByReservationId,
    getStringProp('serviceOrderId'),
    (featureFlagService, nestedProductServiceOrders, serviceOrderId) =>
        featureFlagService.hasFlag(FeatureFlag.linkProductsToReservationsEnabled)
            ? [...nestedProductServiceOrders.map((ps) => ps.id), serviceOrderId]
            : [serviceOrderId]
);

export const getBillOrderItemIdsByServiceOrderId = createSelector(getBillOrderItemsByServiceOrderId, map('id'));
export const getBillOrderItemIdsByServiceOrderIds = createSelector(getBillOrderItemsByServiceOrderIds, map('id'));

export const getBillNestedOrderItemIdsByServiceOrderId = createSelector(getBillOrderItemsByServiceOrderIds, map('id'));

const getOrderItemsGroupedByType = createSelector(getBillOrderItemsByServiceOrderId, groupBy<OrderItem>('type'));

export const getGroupedOrderItemsTypes = createSelector(
    getOrderItemsGroupedByType,
    (orderItems) => keys(orderItems) as OrderItemType[]
);

const getOrderItemsByType = createSelector(getTypeFromProps, getOrderItemsGroupedByType, (type, orderItemsMap) =>
    get(type, orderItemsMap)
);

const getOrderItemsGroupedByProduct = createSelector(getTypeFromProps, getOrderItemsByType, (type, orderItems) =>
    groupBy<OrderItem>(mapTypeToGroupingFunc[type], orderItems)
);

export const getGroupedOrderItemsGroupIds = createSelector(getOrderItemsGroupedByProduct, keys);

export const getOrderItemsByGroupId = createSelector(
    getStringProp('groupId'),
    (state, { ageCategoryId }: { ageCategoryId?: string }) => ageCategoryId,
    getOrderItemsGroupedByProduct,
    (groupId, ageCategoryId, groupedItems) => {
        const orderItems = get(groupId, groupedItems) || [];

        return ageCategoryId
            ? orderItems.filter(
                  (orderItem) => isProductOrderItem(orderItem) && orderItem.data.value.ageCategoryId === ageCategoryId
              )
            : orderItems;
    }
);

export const getGroupedOrderItemIds = createSelector(getOrderItemsByGroupId, map('id'));

export const getGroupedSortedOrderItems = createSelector(getOrderItemsByGroupId, sortBy<OrderItem>('startUtc'));

export const getOrderItemIdsByMonth = createSelector(getMonthOrdersServiceOrders, map('id'));

export const getUnlinkedOrderedServiceOrders = createSelector(
    getOrderedServiceOrders,
    getReservationIdsByBillIds,
    getFeatureFlagService,
    (serviceOrders, reservationIds, featureFlagService) =>
        featureFlagService.hasFlag(FeatureFlag.linkProductsToReservationsEnabled)
            ? array.filter<ServiceOrder>((serviceOrder) => {
                  const isProductServiceOrderType = isProductServiceOrder(serviceOrder);
                  const linkedReservationId = isProductServiceOrderType && serviceOrder.data.value.linkedReservationId;

                  return (
                      !isProductServiceOrderType ||
                      !linkedReservationId ||
                      !reservationIds.includes(linkedReservationId)
                  );
              })(serviceOrders)
            : serviceOrders
);

export const getOrderedServiceOrdersById = createSelector(getOrderedServiceOrders, getCollectionIds);

export const doAllOrderItemsHaveSameNotes = createSelector(getOrderItemsByGroupId, (orderItems) =>
    every((item) => item.notes === orderItems[0].notes, orderItems)
);
